import React, { useCallback, useEffect, useState } from "react";
import { api, getToken } from "../../../software/api";
import { toast } from "sonner";
import { Plus, Search, Trash2, Pencil, AlertTriangle, X, Package, Upload, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", sku: "", category: "", brand: "", supplier: "", price: "", cost_price: "", qty: "", stock_alert: "", barcode: "", location: "", gst: true, active: true, image_url: "", specs: "", showroom: false };
const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const Field = ({ label, children }) => (
  <div><label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>{children}</div>
);
const inp = "w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-sky-400 focus:ring-2 focus:ring-sky-100 outline-none";

// Searchable, free-type dealer combobox (not locked — type letter by letter to filter)
const DealerCombo = ({ value, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const boxRef = React.useRef(null);
  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const q = (value || "").toLowerCase();
  const matches = q ? options.filter((o) => o.toLowerCase().includes(q)) : options;
  return (
    <div className="relative" ref={boxRef}>
      <input
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        data-testid="product-dealer"
        autoComplete="off"
        placeholder="Type to search dealers…"
        className={inp}
      />
      {open && matches.length > 0 && (
        <div data-testid="dealer-options" className="absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg">
          {matches.slice(0, 50).map((o) => (
            <button
              type="button"
              key={o}
              data-testid="dealer-option"
              onClick={() => { onChange(o); setOpen(false); }}
              className="block w-full px-3 py-1.5 text-left text-sm hover:bg-sky-50 truncate"
            >{o}</button>
          ))}
        </div>
      )}
    </div>
  );
};

export default function Inventory({ isAdmin, onExit }) {
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [dealers, setDealers] = useState([]);

  const uploadFile = async (f) => {
    if (!f) return;
    if (!f.type?.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await fetch(`${API}/uploads`, { method: "POST", headers: { Authorization: `Bearer ${getToken()}` }, body: fd });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "Upload failed");
      setForm((s) => ({ ...s, image_url: `${process.env.REACT_APP_BACKEND_URL}${d.url}` }));
      toast.success("Photo uploaded");
    } catch (err) { toast.error(err.message); }
    finally { setUploading(false); }
  };
  const onUpload = async (e) => { await uploadFile(e.target.files?.[0]); e.target.value = ""; };
  const onDrop = async (e) => { e.preventDefault(); setDragging(false); await uploadFile(e.dataTransfer.files?.[0]); };

  const load = useCallback(async () => {
    setProducts(await api("GET", `/products${q ? `?q=${encodeURIComponent(q)}` : ""}`));
  }, [q]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  useEffect(() => { api("GET", "/products/dealers").then((d) => setDealers(d?.dealers || [])).catch(() => {}); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setModal(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...EMPTY, ...p, price: p.price ?? "", cost_price: p.cost_price ?? "", qty: p.qty ?? "", stock_alert: p.stock_alert ?? "" }); setModal(true); };

  const save = async (e) => {
    e.preventDefault();
    const body = {
      name: form.name, sku: form.sku, category: form.category, brand: form.brand, supplier: form.supplier,
      price: Number(form.price) || 0, cost_price: Number(form.cost_price) || 0,
      qty: parseInt(form.qty, 10) || 0, stock_alert: parseInt(form.stock_alert, 10) || 0,
      barcode: form.barcode, location: form.location, gst: !!form.gst, active: !!form.active,
      image_url: form.image_url || "", specs: form.specs || "", showroom: !!form.showroom,
    };
    if (editing) await api("PATCH", `/products/${editing.id}`, body);
    else await api("POST", "/products", body);
    setModal(false); load();
  };
  const del = async (p) => {
    if (!window.confirm(`Delete "${p.name}" from inventory?`)) return;
    try { await api("DELETE", `/products/${p.id}`); load(); }
    catch (e) { alert(e.message); }
  };

  return (
    <div data-testid="inventory-panel">
      {onExit && <button onClick={onExit} data-testid="inventory-switchboard" className="inline-flex items-center gap-1.5 rounded-lg border-2 border-[#123a63] bg-white px-4 py-2 text-sm font-black text-[#123a63] hover:bg-slate-50 mb-3"><ArrowLeft className="h-4 w-4" /> Switch Menu</button>}
      <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
        <h1 className="font-display text-2xl font-bold text-slate-900">Inventory & stock</h1>
        <button onClick={openNew} data-testid="add-product-btn" className="inline-flex items-center gap-2 rounded-full bg-slate-900 hover:bg-sky-700 text-white text-sm font-semibold px-4 py-2.5 transition-colors"><Plus className="h-4 w-4" /> Add product</button>
      </div>

      <div className="relative mb-4 max-w-md">
        <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="product-search" placeholder="Search name, SKU or barcode…" className="w-full rounded-xl border border-slate-200 pl-9 pr-3 py-2.5 text-sm focus:border-sky-400 focus:ring-2 focus:ring-sky-100 outline-none" />
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="hidden sm:grid grid-cols-[2fr_1fr_1fr_0.8fr_0.8fr_auto] gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-100 font-mono2 text-[10px] uppercase tracking-widest text-slate-400">
          <span>Product</span><span>SKU / Barcode</span><span>Category</span><span>Price</span><span>Qty</span><span></span>
        </div>
        {products.length === 0 && <p className="text-sm text-slate-400 p-5">No products yet — add your first.</p>}
        <div className="divide-y divide-slate-100">
          {products.map((p) => (
            <div key={p.id} data-testid="product-row" className="grid sm:grid-cols-[2fr_1fr_1fr_0.8fr_0.8fr_auto] gap-2 px-4 py-3 text-sm items-center">
              <span className="font-semibold text-slate-800 flex items-center gap-2"><Package className="h-4 w-4 text-slate-300 shrink-0" />{p.name}{!p.active && <span className="text-[10px] text-slate-400">(inactive)</span>}</span>
              <span className="text-slate-500 font-mono2 text-xs truncate">{p.sku || "—"}{p.barcode ? ` · ${p.barcode}` : ""}</span>
              <span className="text-slate-500">{p.category || "—"}</span>
              <span className="font-mono2 text-slate-700">{money(p.price)}</span>
              <span className={`font-mono2 font-semibold inline-flex items-center gap-1 ${p.low_stock ? "text-amber-600" : "text-slate-700"}`}>{p.low_stock && <AlertTriangle className="h-3.5 w-3.5" />}{p.qty}</span>
              <div className="flex items-center gap-1 justify-end">
                <button onClick={() => openEdit(p)} data-testid="edit-product-btn" className="p-1.5 rounded-lg text-slate-400 hover:text-sky-600 hover:bg-slate-100"><Pencil className="h-4 w-4" /></button>
                {isAdmin && <button onClick={() => del(p)} data-testid="delete-product-btn" className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" data-testid="modal-product">
          <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-4 border-b border-slate-100"><span className="font-display font-bold text-slate-900">{editing ? "Edit product" : "Add product"}</span><button onClick={() => setModal(false)} className="rounded-full border border-slate-200 p-1.5 hover:bg-slate-50"><X className="h-4 w-4" /></button></div>
            <form onSubmit={save} className="p-5 grid grid-cols-2 gap-3">
              <div className="col-span-2"><Field label="Name *"><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-name" className={inp} /></Field></div>
              <Field label="SKU / Part no."><input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} data-testid="product-sku" className={inp} /></Field>
              <Field label="Barcode"><input value={form.barcode} onChange={(e) => setForm({ ...form, barcode: e.target.value })} data-testid="product-barcode" className={inp} /></Field>
              <Field label="Category"><input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inp} /></Field>
              <Field label="Brand"><input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className={inp} /></Field>
              <div className="col-span-2"><Field label="Dealer"><DealerCombo value={form.supplier} options={dealers} onChange={(v) => setForm({ ...form, supplier: v })} /></Field></div>
              <Field label="Sell price (inc GST)"><input type="number" step="any" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="product-price" className={inp} /></Field>
              <Field label="Cost price"><input type="number" step="any" value={form.cost_price} onChange={(e) => setForm({ ...form, cost_price: e.target.value })} className={inp} /></Field>
              <Field label="Qty on hand"><input type="number" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} data-testid="product-qty" className={inp} /></Field>
              <Field label="Low-stock alert"><input type="number" value={form.stock_alert} onChange={(e) => setForm({ ...form, stock_alert: e.target.value })} data-testid="product-stock_alert" className={inp} /></Field>
              <Field label="Location"><input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className={inp} /></Field>
              <label className="flex items-center gap-2 text-sm mt-6"><input type="checkbox" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active</label>
              <div className="col-span-2 border-t border-slate-100 mt-2 pt-3"><p className="text-[10px] font-mono2 uppercase tracking-widest text-indigo-600 mb-2">Showroom (spas / saunas / gazebos)</p></div>
              <div className="col-span-2"><Field label="Image (drag & drop, upload from device, or paste URL)">
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                  onDragLeave={(e) => { e.preventDefault(); setDragging(false); }}
                  onDrop={onDrop}
                  data-testid="product-image-dropzone"
                  className={`rounded-xl border-2 border-dashed p-3 transition-colors ${dragging ? "border-sky-400 bg-sky-50" : "border-slate-200 bg-slate-50/60"}`}
                >
                  <div className="flex items-center gap-2">
                    <input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} data-testid="product-image-url" className={`${inp} bg-white`} placeholder="Drop image here, or paste URL…" />
                    <label className={`shrink-0 inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 ${uploading ? "opacity-50 pointer-events-none" : ""}`} data-testid="product-image-upload-label">
                      <Upload className="h-4 w-4" /> {uploading ? "…" : "Upload"}
                      <input type="file" accept="image/*" className="hidden" data-testid="product-image-file" onChange={onUpload} />
                    </label>
                  </div>
                  {form.image_url
                    ? <img src={form.image_url} alt="" className="mt-2 h-24 w-24 object-cover rounded-lg border border-slate-200" />
                    : <p className="mt-2 text-xs text-slate-400">Drag an image file onto this box to upload it.</p>}
                </div>
              </Field></div>
              <div className="col-span-2"><Field label="Specifications"><textarea value={form.specs} onChange={(e) => setForm({ ...form, specs: e.target.value })} rows={3} className={inp} placeholder="Seats 6 · 45 jets · 2.2m x 2.2m · 1,300L" /></Field></div>
              <label className="col-span-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.showroom} onChange={(e) => setForm({ ...form, showroom: e.target.checked })} data-testid="product-showroom" /> Show in Showroom catalogue</label>
              <div className="col-span-2"><button data-testid="save-product-btn" className="mt-3 w-full rounded-full bg-slate-900 hover:bg-sky-700 text-white text-sm font-semibold py-2.5 transition-colors">{editing ? "Save changes" : "Add product"}</button></div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
